import { z } from 'zod';
import validator from 'validator';
import { crossword_data_schema } from '../../types/crossword.js';
import { crossword_solution_data_schema } from '../../types/crossword-solution.js';
export const transformJsonNull = (v) => {
    if (!v || v === 'DbNull')
        return null;
    if (v === 'JsonNull')
        return null;
    return v;
};
export const JsonValueSchema = z.lazy(() => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.literal(null),
    z.record(z.lazy(() => JsonValueSchema.optional())),
    z.array(z.lazy(() => JsonValueSchema)),
]));
export const NullableJsonValue = z
    .union([JsonValueSchema, z.literal('DbNull'), z.literal('JsonNull')])
    .nullable()
    .transform((v) => transformJsonNull(v));
export const InputJsonValueSchema = z.lazy(() => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({ toJSON: z.function(z.tuple([]), z.any()) }),
    z.record(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
    z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
]));
// DECIMAL
//------------------------------------------------------
export const DecimalJsLikeSchema = z.object({
    d: z.array(z.number()),
    e: z.number(),
    s: z.number(),
    toFixed: z.function(z.tuple([]), z.string()),
});
export const DECIMAL_STRING_REGEX = /^(?:-?Infinity|NaN|-?(?:0[bB][01]+(?:\.[01]+)?(?:[pP][-+]?\d+)?|0[oO][0-7]+(?:\.[0-7]+)?(?:[pP][-+]?\d+)?|0[xX][\da-fA-F]+(?:\.[\da-fA-F]+)?(?:[pP][-+]?\d+)?|(?:\d+|\d*\.\d+)(?:[eE][-+]?\d+)?))$/;
export const isValidDecimalInput = (v) => {
    if (v === undefined || v === null)
        return false;
    return ((typeof v === 'object' && 'd' in v && 'e' in v && 's' in v && 'toFixed' in v) ||
        (typeof v === 'string' && DECIMAL_STRING_REGEX.test(v)) ||
        typeof v === 'number');
};
/////////////////////////////////////////
// ENUMS
/////////////////////////////////////////
export const TransactionIsolationLevelSchema = z.enum(['ReadUncommitted', 'ReadCommitted', 'RepeatableRead', 'Serializable']);
export const Auth_codeScalarFieldEnumSchema = z.enum(['uuid', 'code', 'device_type', 'user_uuid', 'accessed', 'created_at', 'updated_at']);
export const Login_codeScalarFieldEnumSchema = z.enum(['uuid', 'domain_env', 'code', 'email', 'phone_number', 'created_at', 'updated_at']);
export const Credit_voucherScalarFieldEnumSchema = z.enum(['uuid', 'code', 'credits', 'name', 'description', 'uses', 'max_uses', 'max_uses_user', 'user_uuid', 'start_time', 'end_time', 'created_at', 'updated_at']);
export const User_credit_voucher_xrefScalarFieldEnumSchema = z.enum(['uuid', 'user_uuid', 'credit_voucher_uuid', 'created_at']);
export const CrosswordScalarFieldEnumSchema = z.enum(['id', 'uuid', 'version_uuid', 'title', 'short_description', 'long_description', 'data', 'is_published', 'is_trashed', 'is_banned', 'created_at', 'updated_at', 'show_ads', 'strict_mode', 'is_today']);
export const Crossword_categoryScalarFieldEnumSchema = z.enum(['crossword_uuid', 'type', 'created_at', 'updated_at']);
export const Crossword_image_xrefScalarFieldEnumSchema = z.enum(['crossword_uuid', 'upload_uuid']);
export const Crossword_region_xrefScalarFieldEnumSchema = z.enum(['crossword_uuid', 'region_uuid']);
export const Crossword_tag_xrefScalarFieldEnumSchema = z.enum(['crossword_uuid', 'tag_uuid']);
export const Crossword_person_xrefScalarFieldEnumSchema = z.enum(['crossword_uuid', 'person_uuid']);
export const Crossword_user_xrefScalarFieldEnumSchema = z.enum(['crossword_uuid', 'user_uuid']);
export const Crossword_seriesScalarFieldEnumSchema = z.enum(['uuid', 'name', 'slug', 'description', 'description_rich', 'type', 'user_uuid', 'is_published', 'start_time', 'end_time', 'created_at', 'updated_at']);
export const Crossword_series_crossword_xrefScalarFieldEnumSchema = z.enum(['uuid', 'sequence', 'crossword_series_uuid', 'crossword_uuid']);
export const Crossword_series_image_xrefScalarFieldEnumSchema = z.enum(['crossword_series_uuid', 'upload_uuid']);
export const Crossword_solutionScalarFieldEnumSchema = z.enum(['uuid', 'crossword_uuid', 'user_uuid', 'is_complete', 'has_revealed_any', 'has_revealed_all', 'score', 'data', 'created_at', 'updated_at', 'completed_at']);
export const Email_email_confirmScalarFieldEnumSchema = z.enum(['uuid', 'token', 'user_uuid', 'domain_env', 'email', 'created_at', 'updated_at']);
export const Email_reset_passwordScalarFieldEnumSchema = z.enum(['uuid', 'token', 'email', 'user_uuid', 'domain_env', 'created_at', 'updated_at']);
export const EpisodeScalarFieldEnumSchema = z.enum(['uuid', 'version_uuid', 'title', 'admin_title', 'short_description', 'long_description', 'is_banned', 'is_published', 'is_trashed', 'show_ads', 'premier_date', 'created_at', 'updated_at', 'purchasable', 'purchase_price_default', 'purchase_price_us', 'rentable', 'rent_price_default', 'rent_price_us', 'free_for_subscriber', 'tv_uuid', 'season_uuid', 'number']);
export const Episode_image_xrefScalarFieldEnumSchema = z.enum(['episode_uuid', 'upload_uuid', 'created_at', 'is_thumbnail']);
export const Episode_person_xrefScalarFieldEnumSchema = z.enum(['uuid', 'episode_uuid', 'person_uuid', 'is_actor', 'is_director', 'is_creator']);
export const Episode_trailer_xrefScalarFieldEnumSchema = z.enum(['episode_uuid', 'upload_uuid']);
export const Episode_user_xrefScalarFieldEnumSchema = z.enum(['episode_uuid', 'user_uuid']);
export const Episode_video_xrefScalarFieldEnumSchema = z.enum(['episode_uuid', 'upload_uuid']);
export const GenreScalarFieldEnumSchema = z.enum(['uuid', 'name', 'description', 'created_at', 'updated_at']);
export const LanguageScalarFieldEnumSchema = z.enum(['uuid', 'name', 'description', 'created_at', 'updated_at']);
export const MovieScalarFieldEnumSchema = z.enum(['uuid', 'version_uuid', 'title', 'search_token', 'admin_title', 'short_description', 'long_description', 'is_banned', 'is_published', 'is_trashed', 'show_ads', 'premier_date', 'created_at', 'updated_at', 'purchasable', 'purchase_price_default', 'purchase_price_us', 'rentable', 'rent_price_default', 'rent_price_us', 'owner_user_uuid', 'purchase_commission_percent', 'rent_commission_percent', 'free_for_subscriber']);
export const Movie_genre_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'genre_uuid']);
export const Movie_image_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'upload_uuid', 'is_thumbnail']);
export const Movie_mpaa_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'mpaa_uuid']);
export const Movie_person_xrefScalarFieldEnumSchema = z.enum(['uuid', 'movie_uuid', 'person_uuid', 'is_actor', 'is_director', 'is_creator']);
export const Movie_region_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'region_uuid']);
export const Movie_tag_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'tag_uuid']);
export const Movie_trailer_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'upload_uuid']);
export const Movie_user_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'user_uuid']);
export const Movie_video_xrefScalarFieldEnumSchema = z.enum(['movie_uuid', 'upload_uuid']);
export const MpaaScalarFieldEnumSchema = z.enum(['uuid', 'name', 'description', 'created_at', 'updated_at']);
export const PersonScalarFieldEnumSchema = z.enum(['uuid', 'user_name', 'name', 'summary', 'biography', 'created_at', 'updated_at', 'facebook_user_name', 'twitter_user_name', 'birth_date']);
export const Person_image_xrefScalarFieldEnumSchema = z.enum(['person_uuid', 'upload_uuid']);
export const ProfileScalarFieldEnumSchema = z.enum(['uuid', 'name', 'autoplay_next_episode', 'autoplay_preview', 'is_default', 'user_uuid', 'created_at', 'updated_at']);
export const Profile_avatar_xrefScalarFieldEnumSchema = z.enum(['profile_uuid', 'upload_uuid']);
export const Profile_savedScalarFieldEnumSchema = z.enum(['uuid', 'profile_uuid', 'movie_uuid', 'tv_uuid', 'is_archived', 'created_at', 'updated_at']);
export const Profile_voteScalarFieldEnumSchema = z.enum(['uuid', 'profile_uuid', 'movie_uuid', 'tv_uuid', 'vote', 'created_at', 'updated_at']);
export const Profile_watch_historyScalarFieldEnumSchema = z.enum(['uuid', 'profile_uuid', 'movie_uuid', 'episode_uuid', 'progress', 'created_at', 'updated_at']);
export const RegionScalarFieldEnumSchema = z.enum(['uuid', 'name', 'slug', 'description', 'created_at', 'updated_at']);
export const SavedScalarFieldEnumSchema = z.enum(['uuid', 'user_uuid', 'crossword_uuid', 'is_archived', 'created_at', 'updated_at']);
export const SeasonScalarFieldEnumSchema = z.enum(['uuid', 'version_uuid', 'title', 'admin_title', 'short_description', 'long_description', 'is_banned', 'is_published', 'is_trashed', 'show_ads', 'premier_date', 'created_at', 'updated_at', 'purchasable', 'purchase_price_default', 'purchase_price_us', 'rentable', 'rent_price_default', 'rent_price_us', 'free_for_subscriber', 'tv_uuid', 'number']);
export const Season_image_xrefScalarFieldEnumSchema = z.enum(['season_uuid', 'upload_uuid', 'is_thumbnail']);
export const Season_person_xrefScalarFieldEnumSchema = z.enum(['uuid', 'season_uuid', 'person_uuid', 'is_actor', 'is_director', 'is_creator']);
export const Season_trailer_xrefScalarFieldEnumSchema = z.enum(['season_uuid', 'upload_uuid']);
export const Season_user_xrefScalarFieldEnumSchema = z.enum(['season_uuid', 'user_uuid']);
export const SettingScalarFieldEnumSchema = z.enum(['uuid', 'user_approved_by_default', 'ads', 'ads_adsense', 'created_at', 'updated_at']);
export const Show_seriesScalarFieldEnumSchema = z.enum(['uuid', 'name', 'slug', 'description', 'description_rich', 'type', 'user_uuid', 'is_published', 'start_time', 'end_time', 'created_at', 'updated_at']);
export const Show_series_image_xrefScalarFieldEnumSchema = z.enum(['show_series_uuid', 'upload_uuid']);
export const Show_series_show_xrefScalarFieldEnumSchema = z.enum(['uuid', 'sequence', 'show_series_uuid', 'movie_uuid', 'tv_uuid', 'season_uuid', 'episode_uuid', 'series_uuid', 'created_at', 'updated_at']);
export const TagScalarFieldEnumSchema = z.enum(['uuid', 'name', 'slug', 'description', 'created_at', 'updated_at']);
export const TvScalarFieldEnumSchema = z.enum(['uuid', 'version_uuid', 'title', 'admin_title', 'search_token', 'short_description', 'long_description', 'is_banned', 'is_published', 'is_trashed', 'show_ads', 'premier_date', 'created_at', 'updated_at', 'purchasable', 'purchase_price_default', 'purchase_price_us', 'rentable', 'rent_price_default', 'rent_price_us', 'owner_user_uuid', 'purchase_commission_percent', 'rent_commission_percent', 'free_for_subscriber']);
export const Tv_genre_xrefScalarFieldEnumSchema = z.enum(['tv_uuid', 'genre_uuid']);
export const Tv_image_xrefScalarFieldEnumSchema = z.enum(['tv_uuid', 'upload_uuid', 'is_thumbnail']);
export const Tv_mpaa_xrefScalarFieldEnumSchema = z.enum(['tv_uuid', 'mpaa_uuid']);
export const Tv_person_xrefScalarFieldEnumSchema = z.enum(['uuid', 'tv_uuid', 'person_uuid', 'is_actor', 'is_director', 'is_creator']);
export const Tv_region_xrefScalarFieldEnumSchema = z.enum(['tv_uuid', 'region_uuid']);
export const Tv_tag_xrefScalarFieldEnumSchema = z.enum(['tv_uuid', 'tag_uuid']);
export const Tv_trailer_xrefScalarFieldEnumSchema = z.enum(['tv_uuid', 'upload_uuid']);
export const Tv_user_xrefScalarFieldEnumSchema = z.enum(['tv_uuid', 'user_uuid']);
export const UploadScalarFieldEnumSchema = z.enum(['uuid', 'google_storage_key', 'name', 'description', 'created_at', 'updated_at', 'is_avatar', 'sequence', 'type', 'content_type', 'is_public', 'mux_asset_id', 'mux_asset_environment', 'user_uuid', 'duration_seconds', 'mux_playback_id']);
export const Upload_language_xrefScalarFieldEnumSchema = z.enum(['upload_uuid', 'language_uuid']);
export const UserScalarFieldEnumSchema = z.enum(['uuid', 'domain_env', 'name', 'user_name', 'password', 'email', 'summary', 'biography', 'is_banned', 'is_approved', 'email_is_confirmed', 'created_at', 'updated_at', 'facebook_user_name', 'twitter_user_name', 'coins', 'facebook_id', 'google_id', 'birth_date', 'sc_stripe_customer_id', 'sc_get_started_completed', 'apple_user_id', 'credits', 'phone_number', 'phone_number_verified']);
export const User_image_xrefScalarFieldEnumSchema = z.enum(['user_uuid', 'upload_uuid']);
export const User_permissionScalarFieldEnumSchema = z.enum(['user_uuid', 'permission_role', 'created_at', 'updated_at']);
export const User_show_purchaseScalarFieldEnumSchema = z.enum(['uuid', 'user_uuid', 'owner_user_uuid', 'price', 'commission_percent', 'movie_uuid', 'episode_uuid', 'season_uuid', 'tv_uuid', 'country_code', 'created_at', 'updated_at']);
export const User_show_rentScalarFieldEnumSchema = z.enum(['uuid', 'user_uuid', 'owner_user_uuid', 'price', 'commission_percent', 'movie_uuid', 'episode_uuid', 'season_uuid', 'tv_uuid', 'country_code', 'created_at', 'updated_at']);
export const Sc_businessScalarFieldEnumSchema = z.enum(['id', 'platform', 'user_uuid', 'instagram_account_id', 'instagram_long_lived_access_token', 'instagram_long_lived_access_token_expires_at', 'instagram_permissions_csv', 'created_at', 'updated_at']);
export const Sc_business_workflowScalarFieldEnumSchema = z.enum(['business_id', 'instructions_text', 'reply_as_message', 'created_at', 'updated_at']);
export const Sc_post_workflowScalarFieldEnumSchema = z.enum(['post_id', 'business_id', 'instructions_text', 'reply_as_message', 'created_at', 'updated_at']);
export const Sc_commentScalarFieldEnumSchema = z.enum(['uuid', 'business_id', 'comment_id', 'commenter_id', 'reply_as_message', 'reply_as_message_comment_text', 'reply_text', 'reply_attempts', 'reply_posted', 'reply_at', 'workflow_business_id', 'workflow_post_id', 'created_at', 'updated_at']);
export const SortOrderSchema = z.enum(['asc', 'desc']);
export const JsonNullValueInputSchema = z.enum(['JsonNull',]).transform((value) => (value === 'JsonNull' ? null : value));
export const NullsOrderSchema = z.enum(['first', 'last']);
export const JsonNullValueFilterSchema = z.enum(['DbNull', 'JsonNull', 'AnyNull',]).transform((value) => value === 'JsonNull' ? null : value === 'DbNull' ? null : value === 'AnyNull' ? null : value);
export const EnumUploadTypeSchema = z.enum(['image', 'video']);
export const EnumCrosswordSeriesTypeSchema = z.enum(['top', 'trending', 'related', 'editor_picks', 'home_page', 'challenge', 'other']);
export const EnumShowSeriesTypeSchema = z.enum(['top', 'trending', 'related', 'editor_picks', 'home_page', 'movie_page', 'tv_page', 'other']);
export const EnumPermissionRoleSchema = z.enum(['ADMIN', 'ROOT_ADMIN', 'CROSSWORD_READ', 'CROSSWORD_WRITE', 'USER_READ', 'USER_WRITE', 'SHOW_READ', 'SHOW_WRITE', 'UPLOAD_READ', 'UPLOAD_WRITE', 'CONSOLE_ACCESS', 'PURCHASE_READ', 'PURCHASE_WRITE', 'CREDIT_VOUCHER_READ', 'CREDIT_VOUCHER_WRITE']);
export const EnumCrosswordCategoryTypeSchema = z.enum(['intellectual', 'swahili', 'general', 'hard']);
export const EnumDomainEnvDbSchema = z.enum(['pendwa_org', 'pendwa_app', 'africacrosswords_com', 'swiftcomment_com']);
export const EnumSCPlatformDbSchema = z.enum(['instagram']);
/////////////////////////////////////////
// MODELS
/////////////////////////////////////////
/////////////////////////////////////////
// AUTH CODE SCHEMA
/////////////////////////////////////////
export const auth_codeSchema = z.object({
    uuid: z.string().uuid(),
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9]*$/, { message: "Must be capitalized alphanumeric characters" }).min(5).max(5),
    device_type: z.string().trim().min(1).max(255),
    user_uuid: z.string().nullish(),
    accessed: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// AUTH CODE PARTIAL SCHEMA
/////////////////////////////////////////
export const auth_codePartialSchema = auth_codeSchema.partial();
// AUTH CODE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const auth_codeOptionalDefaultsSchema = auth_codeSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    accessed: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// LOGIN CODE SCHEMA
/////////////////////////////////////////
export const login_codeSchema = z.object({
    domain_env: EnumDomainEnvDbSchema,
    uuid: z.string().uuid(),
    code: z.number().int().gte(10000).lte(99999),
    email: z.string().trim().min(0).max(255).refine((val) => val ? validator.isEmail(val) : true, { message: 'Must be a valid email.' }).transform(value => value === '' ? null : value).nullish(),
    phone_number: z.string().trim().min(0).max(255).refine((val) => val ? validator.isMobilePhone(val) : true, { message: 'Must be a valid phone number.' }).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// LOGIN CODE CUSTOM VALIDATORS SCHEMA
/////////////////////////////////////////
export const login_codeCustomValidatorsSchema = login_codeSchema;
/////////////////////////////////////////
// LOGIN CODE PARTIAL SCHEMA
/////////////////////////////////////////
export const login_codePartialSchema = login_codeSchema.partial();
// LOGIN CODE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const login_codeOptionalDefaultsSchema = login_codeSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// CREDIT VOUCHER SCHEMA
/////////////////////////////////////////
export const credit_voucherSchema = z.object({
    uuid: z.string().uuid(),
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9]*$/, { message: "Has to be a capitalized (herufi kubwa) alphanumeric" }).min(5).max(10),
    credits: z.number().min(0.01).max(30),
    name: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    uses: z.number().int().min(1).max(1000),
    max_uses: z.number().int().min(1).max(1000),
    max_uses_user: z.number().int().min(1).max(1000),
    user_uuid: z.string().nullish(),
    start_time: z.coerce.date(),
    end_time: z.coerce.date(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// CREDIT VOUCHER PARTIAL SCHEMA
/////////////////////////////////////////
export const credit_voucherPartialSchema = credit_voucherSchema.partial();
// CREDIT VOUCHER OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const credit_voucherOptionalDefaultsSchema = credit_voucherSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    uses: z.number().int().min(1).max(1000).optional(),
    max_uses: z.number().int().min(1).max(1000).optional(),
    max_uses_user: z.number().int().min(1).max(1000).optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// USER CREDIT VOUCHER XREF SCHEMA
/////////////////////////////////////////
export const user_credit_voucher_xrefSchema = z.object({
    uuid: z.string().uuid(),
    user_uuid: z.string().nullish(),
    credit_voucher_uuid: z.string().nullish(),
    created_at: z.coerce.date(),
});
/////////////////////////////////////////
// USER CREDIT VOUCHER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const user_credit_voucher_xrefPartialSchema = user_credit_voucher_xrefSchema.partial();
// USER CREDIT VOUCHER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const user_credit_voucher_xrefOptionalDefaultsSchema = user_credit_voucher_xrefSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// CROSSWORD SCHEMA
/////////////////////////////////////////
export const crosswordSchema = z.object({
    id: z.number().int(),
    uuid: z.string().uuid(),
    version_uuid: z.string().nullish(),
    title: z.string().trim().min(1).max(255),
    short_description: z.string().trim().min(0).max(500).transform(value => value === '' ? null : value).nullish(),
    long_description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    /**
     * [CrosswordData]
     */
    data: crossword_data_schema,
    is_published: z.boolean(),
    is_trashed: z.boolean(),
    is_banned: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    show_ads: z.boolean(),
    strict_mode: z.boolean(),
    is_today: z.boolean(),
});
/////////////////////////////////////////
// CROSSWORD CUSTOM VALIDATORS SCHEMA
/////////////////////////////////////////
export const crosswordCustomValidatorsSchema = crosswordSchema;
/////////////////////////////////////////
// CROSSWORD PARTIAL SCHEMA
/////////////////////////////////////////
export const crosswordPartialSchema = crosswordSchema.partial();
// CROSSWORD OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crosswordOptionalDefaultsSchema = crosswordSchema.merge(z.object({
    id: z.number().int().optional(),
    uuid: z.string().uuid().optional(),
    is_published: z.boolean().optional(),
    is_trashed: z.boolean().optional(),
    is_banned: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
    show_ads: z.boolean().optional(),
    strict_mode: z.boolean().optional(),
    is_today: z.boolean().optional(),
}));
/////////////////////////////////////////
// CROSSWORD CATEGORY SCHEMA
/////////////////////////////////////////
export const crossword_categorySchema = z.object({
    type: EnumCrosswordCategoryTypeSchema,
    crossword_uuid: z.string(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// CROSSWORD CATEGORY PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_categoryPartialSchema = crossword_categorySchema.partial();
// CROSSWORD CATEGORY OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_categoryOptionalDefaultsSchema = crossword_categorySchema.merge(z.object({
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// CROSSWORD IMAGE XREF SCHEMA
/////////////////////////////////////////
export const crossword_image_xrefSchema = z.object({
    crossword_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// CROSSWORD IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_image_xrefPartialSchema = crossword_image_xrefSchema.partial();
// CROSSWORD IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_image_xrefOptionalDefaultsSchema = crossword_image_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// CROSSWORD REGION XREF SCHEMA
/////////////////////////////////////////
export const crossword_region_xrefSchema = z.object({
    crossword_uuid: z.string(),
    region_uuid: z.string(),
});
/////////////////////////////////////////
// CROSSWORD REGION XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_region_xrefPartialSchema = crossword_region_xrefSchema.partial();
// CROSSWORD REGION XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_region_xrefOptionalDefaultsSchema = crossword_region_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// CROSSWORD TAG XREF SCHEMA
/////////////////////////////////////////
export const crossword_tag_xrefSchema = z.object({
    crossword_uuid: z.string(),
    tag_uuid: z.string(),
});
/////////////////////////////////////////
// CROSSWORD TAG XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_tag_xrefPartialSchema = crossword_tag_xrefSchema.partial();
// CROSSWORD TAG XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_tag_xrefOptionalDefaultsSchema = crossword_tag_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// CROSSWORD PERSON XREF SCHEMA
/////////////////////////////////////////
export const crossword_person_xrefSchema = z.object({
    crossword_uuid: z.string(),
    person_uuid: z.string(),
});
/////////////////////////////////////////
// CROSSWORD PERSON XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_person_xrefPartialSchema = crossword_person_xrefSchema.partial();
// CROSSWORD PERSON XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_person_xrefOptionalDefaultsSchema = crossword_person_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// CROSSWORD USER XREF SCHEMA
/////////////////////////////////////////
export const crossword_user_xrefSchema = z.object({
    crossword_uuid: z.string(),
    user_uuid: z.string(),
});
/////////////////////////////////////////
// CROSSWORD USER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_user_xrefPartialSchema = crossword_user_xrefSchema.partial();
// CROSSWORD USER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_user_xrefOptionalDefaultsSchema = crossword_user_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// CROSSWORD SERIES SCHEMA
/////////////////////////////////////////
export const crossword_seriesSchema = z.object({
    type: EnumCrosswordSeriesTypeSchema,
    uuid: z.string().uuid(),
    name: z.string().trim().min(1).max(255),
    slug: z.string().trim().regex(/^[a-zA-Z0-9_-]*$/, { message: "Can only contain letters, numbers, underscores and hyphens" }).min(1).max(255),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    description_rich: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    user_uuid: z.string().nullish(),
    is_published: z.boolean(),
    start_time: z.coerce.date(),
    end_time: z.coerce.date(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// CROSSWORD SERIES PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_seriesPartialSchema = crossword_seriesSchema.partial();
// CROSSWORD SERIES OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_seriesOptionalDefaultsSchema = crossword_seriesSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_published: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// CROSSWORD SERIES CROSSWORD XREF SCHEMA
/////////////////////////////////////////
export const crossword_series_crossword_xrefSchema = z.object({
    uuid: z.string().uuid(),
    sequence: z.number().min(1),
    crossword_series_uuid: z.string(),
    crossword_uuid: z.string(),
});
/////////////////////////////////////////
// CROSSWORD SERIES CROSSWORD XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_series_crossword_xrefPartialSchema = crossword_series_crossword_xrefSchema.partial();
// CROSSWORD SERIES CROSSWORD XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_series_crossword_xrefOptionalDefaultsSchema = crossword_series_crossword_xrefSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
}));
/////////////////////////////////////////
// CROSSWORD SERIES IMAGE XREF SCHEMA
/////////////////////////////////////////
export const crossword_series_image_xrefSchema = z.object({
    crossword_series_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// CROSSWORD SERIES IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_series_image_xrefPartialSchema = crossword_series_image_xrefSchema.partial();
// CROSSWORD SERIES IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_series_image_xrefOptionalDefaultsSchema = crossword_series_image_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// CROSSWORD SOLUTION SCHEMA
/////////////////////////////////////////
export const crossword_solutionSchema = z.object({
    uuid: z.string().uuid(),
    crossword_uuid: z.string().nullish(),
    user_uuid: z.string(),
    is_complete: z.boolean(),
    has_revealed_any: z.boolean(),
    has_revealed_all: z.boolean(),
    score: z.number().int(),
    /**
     * [CrosswordSolutionData]
     */
    data: crossword_solution_data_schema,
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    completed_at: z.coerce.date().nullish(),
});
/////////////////////////////////////////
// CROSSWORD SOLUTION CUSTOM VALIDATORS SCHEMA
/////////////////////////////////////////
export const crossword_solutionCustomValidatorsSchema = crossword_solutionSchema;
/////////////////////////////////////////
// CROSSWORD SOLUTION PARTIAL SCHEMA
/////////////////////////////////////////
export const crossword_solutionPartialSchema = crossword_solutionSchema.partial();
// CROSSWORD SOLUTION OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const crossword_solutionOptionalDefaultsSchema = crossword_solutionSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_complete: z.boolean().optional(),
    has_revealed_any: z.boolean().optional(),
    has_revealed_all: z.boolean().optional(),
    score: z.number().int().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// EMAIL EMAIL CONFIRM SCHEMA
/////////////////////////////////////////
export const email_email_confirmSchema = z.object({
    domain_env: EnumDomainEnvDbSchema,
    uuid: z.string().uuid(),
    token: z.string(),
    user_uuid: z.string(),
    email: z.string().trim().email().min(1).max(255),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// EMAIL EMAIL CONFIRM PARTIAL SCHEMA
/////////////////////////////////////////
export const email_email_confirmPartialSchema = email_email_confirmSchema.partial();
// EMAIL EMAIL CONFIRM OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const email_email_confirmOptionalDefaultsSchema = email_email_confirmSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// EMAIL RESET PASSWORD SCHEMA
/////////////////////////////////////////
export const email_reset_passwordSchema = z.object({
    domain_env: EnumDomainEnvDbSchema,
    uuid: z.string().uuid(),
    token: z.string(),
    email: z.string().trim().email().min(1).max(255),
    user_uuid: z.string(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// EMAIL RESET PASSWORD PARTIAL SCHEMA
/////////////////////////////////////////
export const email_reset_passwordPartialSchema = email_reset_passwordSchema.partial();
// EMAIL RESET PASSWORD OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const email_reset_passwordOptionalDefaultsSchema = email_reset_passwordSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// EPISODE SCHEMA
/////////////////////////////////////////
export const episodeSchema = z.object({
    uuid: z.string().uuid(),
    version_uuid: z.string().nullish(),
    title: z.string().trim().min(1).max(255),
    admin_title: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    short_description: z.string().trim().min(0).max(500).transform(value => value === '' ? null : value).nullish(),
    long_description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    is_banned: z.boolean(),
    is_published: z.boolean(),
    is_trashed: z.boolean(),
    show_ads: z.boolean(),
    premier_date: z.coerce.date(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    purchasable: z.boolean(),
    purchase_price_default: z.number().min(0.0).max(100),
    purchase_price_us: z.number().min(0.0).max(100).nullable(),
    rentable: z.boolean(),
    rent_price_default: z.number().min(0.0).max(100),
    rent_price_us: z.number().min(0.0).max(100).nullable(),
    free_for_subscriber: z.boolean(),
    tv_uuid: z.string().nullish(),
    season_uuid: z.string().nullish(),
    number: z.number().min(1).nullish(),
});
/////////////////////////////////////////
// EPISODE PARTIAL SCHEMA
/////////////////////////////////////////
export const episodePartialSchema = episodeSchema.partial();
// EPISODE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const episodeOptionalDefaultsSchema = episodeSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_banned: z.boolean().optional(),
    is_published: z.boolean().optional(),
    is_trashed: z.boolean().optional(),
    show_ads: z.boolean().optional(),
    premier_date: z.coerce.date().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
    purchasable: z.boolean().optional(),
    purchase_price_default: z.number().min(0.0).max(100).optional(),
    purchase_price_us: z.number().min(0.0).max(100).nullable().optional(),
    rentable: z.boolean().optional(),
    rent_price_default: z.number().min(0.0).max(100).optional(),
    rent_price_us: z.number().min(0.0).max(100).nullable().optional(),
    free_for_subscriber: z.boolean().optional(),
}));
/////////////////////////////////////////
// EPISODE IMAGE XREF SCHEMA
/////////////////////////////////////////
export const episode_image_xrefSchema = z.object({
    episode_uuid: z.string(),
    upload_uuid: z.string(),
    created_at: z.coerce.date(),
    is_thumbnail: z.boolean(),
});
/////////////////////////////////////////
// EPISODE IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const episode_image_xrefPartialSchema = episode_image_xrefSchema.partial();
// EPISODE IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const episode_image_xrefOptionalDefaultsSchema = episode_image_xrefSchema.merge(z.object({
    created_at: z.coerce.date().optional(),
    is_thumbnail: z.boolean().optional(),
}));
/////////////////////////////////////////
// EPISODE PERSON XREF SCHEMA
/////////////////////////////////////////
export const episode_person_xrefSchema = z.object({
    uuid: z.string().uuid(),
    episode_uuid: z.string(),
    person_uuid: z.string(),
    is_actor: z.boolean(),
    is_director: z.boolean(),
    is_creator: z.boolean(),
});
/////////////////////////////////////////
// EPISODE PERSON XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const episode_person_xrefPartialSchema = episode_person_xrefSchema.partial();
// EPISODE PERSON XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const episode_person_xrefOptionalDefaultsSchema = episode_person_xrefSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_actor: z.boolean().optional(),
    is_director: z.boolean().optional(),
    is_creator: z.boolean().optional(),
}));
/////////////////////////////////////////
// EPISODE TRAILER XREF SCHEMA
/////////////////////////////////////////
export const episode_trailer_xrefSchema = z.object({
    episode_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// EPISODE TRAILER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const episode_trailer_xrefPartialSchema = episode_trailer_xrefSchema.partial();
// EPISODE TRAILER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const episode_trailer_xrefOptionalDefaultsSchema = episode_trailer_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// EPISODE USER XREF SCHEMA
/////////////////////////////////////////
export const episode_user_xrefSchema = z.object({
    episode_uuid: z.string(),
    user_uuid: z.string(),
});
/////////////////////////////////////////
// EPISODE USER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const episode_user_xrefPartialSchema = episode_user_xrefSchema.partial();
// EPISODE USER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const episode_user_xrefOptionalDefaultsSchema = episode_user_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// EPISODE VIDEO XREF SCHEMA
/////////////////////////////////////////
export const episode_video_xrefSchema = z.object({
    episode_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// EPISODE VIDEO XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const episode_video_xrefPartialSchema = episode_video_xrefSchema.partial();
// EPISODE VIDEO XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const episode_video_xrefOptionalDefaultsSchema = episode_video_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// GENRE SCHEMA
/////////////////////////////////////////
export const genreSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// GENRE PARTIAL SCHEMA
/////////////////////////////////////////
export const genrePartialSchema = genreSchema.partial();
// GENRE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const genreOptionalDefaultsSchema = genreSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// LANGUAGE SCHEMA
/////////////////////////////////////////
export const languageSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// LANGUAGE PARTIAL SCHEMA
/////////////////////////////////////////
export const languagePartialSchema = languageSchema.partial();
// LANGUAGE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const languageOptionalDefaultsSchema = languageSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// MOVIE SCHEMA
/////////////////////////////////////////
export const movieSchema = z.object({
    uuid: z.string().uuid(),
    version_uuid: z.string().nullish(),
    title: z.string().trim().min(1).max(100),
    search_token: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    admin_title: z.string().trim().min(0).max(100).transform(value => value === '' ? null : value).nullish(),
    short_description: z.string().trim().min(0).max(500).transform(value => value === '' ? null : value).nullish(),
    long_description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    is_banned: z.boolean(),
    is_published: z.boolean(),
    is_trashed: z.boolean(),
    show_ads: z.boolean(),
    premier_date: z.coerce.date(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    purchasable: z.boolean(),
    purchase_price_default: z.number().min(0.0).max(100),
    purchase_price_us: z.number().min(0.0).max(100).nullable(),
    rentable: z.boolean(),
    rent_price_default: z.number().min(0.0).max(100),
    rent_price_us: z.number().min(0.0).max(100).nullable(),
    owner_user_uuid: z.string().nullish(),
    purchase_commission_percent: z.number().min(0).max(100),
    rent_commission_percent: z.number().min(0).max(100),
    free_for_subscriber: z.boolean(),
});
/////////////////////////////////////////
// MOVIE PARTIAL SCHEMA
/////////////////////////////////////////
export const moviePartialSchema = movieSchema.partial();
// MOVIE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movieOptionalDefaultsSchema = movieSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_banned: z.boolean().optional(),
    is_published: z.boolean().optional(),
    is_trashed: z.boolean().optional(),
    show_ads: z.boolean().optional(),
    premier_date: z.coerce.date().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
    purchasable: z.boolean().optional(),
    purchase_price_default: z.number().min(0.0).max(100).optional(),
    purchase_price_us: z.number().min(0.0).max(100).nullable().optional(),
    rentable: z.boolean().optional(),
    rent_price_default: z.number().min(0.0).max(100).optional(),
    rent_price_us: z.number().min(0.0).max(100).nullable().optional(),
    free_for_subscriber: z.boolean().optional(),
}));
/////////////////////////////////////////
// MOVIE GENRE XREF SCHEMA
/////////////////////////////////////////
export const movie_genre_xrefSchema = z.object({
    movie_uuid: z.string(),
    genre_uuid: z.string(),
});
/////////////////////////////////////////
// MOVIE GENRE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_genre_xrefPartialSchema = movie_genre_xrefSchema.partial();
// MOVIE GENRE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_genre_xrefOptionalDefaultsSchema = movie_genre_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// MOVIE IMAGE XREF SCHEMA
/////////////////////////////////////////
export const movie_image_xrefSchema = z.object({
    movie_uuid: z.string(),
    upload_uuid: z.string(),
    is_thumbnail: z.boolean(),
});
/////////////////////////////////////////
// MOVIE IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_image_xrefPartialSchema = movie_image_xrefSchema.partial();
// MOVIE IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_image_xrefOptionalDefaultsSchema = movie_image_xrefSchema.merge(z.object({
    is_thumbnail: z.boolean().optional(),
}));
/////////////////////////////////////////
// MOVIE MPAA XREF SCHEMA
/////////////////////////////////////////
export const movie_mpaa_xrefSchema = z.object({
    movie_uuid: z.string(),
    mpaa_uuid: z.string(),
});
/////////////////////////////////////////
// MOVIE MPAA XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_mpaa_xrefPartialSchema = movie_mpaa_xrefSchema.partial();
// MOVIE MPAA XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_mpaa_xrefOptionalDefaultsSchema = movie_mpaa_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// MOVIE PERSON XREF SCHEMA
/////////////////////////////////////////
export const movie_person_xrefSchema = z.object({
    uuid: z.string().uuid(),
    movie_uuid: z.string(),
    person_uuid: z.string(),
    is_actor: z.boolean(),
    is_director: z.boolean(),
    is_creator: z.boolean(),
});
/////////////////////////////////////////
// MOVIE PERSON XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_person_xrefPartialSchema = movie_person_xrefSchema.partial();
// MOVIE PERSON XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_person_xrefOptionalDefaultsSchema = movie_person_xrefSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_actor: z.boolean().optional(),
    is_director: z.boolean().optional(),
    is_creator: z.boolean().optional(),
}));
/////////////////////////////////////////
// MOVIE REGION XREF SCHEMA
/////////////////////////////////////////
export const movie_region_xrefSchema = z.object({
    movie_uuid: z.string(),
    region_uuid: z.string(),
});
/////////////////////////////////////////
// MOVIE REGION XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_region_xrefPartialSchema = movie_region_xrefSchema.partial();
// MOVIE REGION XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_region_xrefOptionalDefaultsSchema = movie_region_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// MOVIE TAG XREF SCHEMA
/////////////////////////////////////////
export const movie_tag_xrefSchema = z.object({
    movie_uuid: z.string(),
    tag_uuid: z.string(),
});
/////////////////////////////////////////
// MOVIE TAG XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_tag_xrefPartialSchema = movie_tag_xrefSchema.partial();
// MOVIE TAG XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_tag_xrefOptionalDefaultsSchema = movie_tag_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// MOVIE TRAILER XREF SCHEMA
/////////////////////////////////////////
export const movie_trailer_xrefSchema = z.object({
    movie_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// MOVIE TRAILER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_trailer_xrefPartialSchema = movie_trailer_xrefSchema.partial();
// MOVIE TRAILER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_trailer_xrefOptionalDefaultsSchema = movie_trailer_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// MOVIE USER XREF SCHEMA
/////////////////////////////////////////
export const movie_user_xrefSchema = z.object({
    movie_uuid: z.string(),
    user_uuid: z.string(),
});
/////////////////////////////////////////
// MOVIE USER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_user_xrefPartialSchema = movie_user_xrefSchema.partial();
// MOVIE USER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_user_xrefOptionalDefaultsSchema = movie_user_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// MOVIE VIDEO XREF SCHEMA
/////////////////////////////////////////
export const movie_video_xrefSchema = z.object({
    movie_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// MOVIE VIDEO XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const movie_video_xrefPartialSchema = movie_video_xrefSchema.partial();
// MOVIE VIDEO XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const movie_video_xrefOptionalDefaultsSchema = movie_video_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// MPAA SCHEMA
/////////////////////////////////////////
export const mpaaSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// MPAA PARTIAL SCHEMA
/////////////////////////////////////////
export const mpaaPartialSchema = mpaaSchema.partial();
// MPAA OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const mpaaOptionalDefaultsSchema = mpaaSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// PERSON SCHEMA
/////////////////////////////////////////
export const personSchema = z.object({
    uuid: z.string().uuid(),
    user_name: z.string().trim().regex(/^[a-zA-Z0-9_]*$/, { message: "Can only have letters, numbers and underscores" }).min(4).max(255),
    name: z.string().trim().min(1).max(255),
    summary: z.string().trim().min(0).max(500).transform(value => value === '' ? null : value).nullish(),
    biography: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    facebook_user_name: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    twitter_user_name: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    birth_date: z.coerce.date().nullish(),
});
/////////////////////////////////////////
// PERSON PARTIAL SCHEMA
/////////////////////////////////////////
export const personPartialSchema = personSchema.partial();
// PERSON OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const personOptionalDefaultsSchema = personSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// PERSON IMAGE XREF SCHEMA
/////////////////////////////////////////
export const person_image_xrefSchema = z.object({
    person_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// PERSON IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const person_image_xrefPartialSchema = person_image_xrefSchema.partial();
// PERSON IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const person_image_xrefOptionalDefaultsSchema = person_image_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// PROFILE SCHEMA
/////////////////////////////////////////
export const profileSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string().trim().regex(/^[a-zA-Z0-9 ]*$/, { message: "Can only have letters, numbers, and spaces" }).min(1).max(255),
    autoplay_next_episode: z.boolean(),
    autoplay_preview: z.boolean(),
    is_default: z.boolean(),
    user_uuid: z.string(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// PROFILE PARTIAL SCHEMA
/////////////////////////////////////////
export const profilePartialSchema = profileSchema.partial();
// PROFILE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const profileOptionalDefaultsSchema = profileSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    autoplay_next_episode: z.boolean().optional(),
    autoplay_preview: z.boolean().optional(),
    is_default: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// PROFILE AVATAR XREF SCHEMA
/////////////////////////////////////////
export const profile_avatar_xrefSchema = z.object({
    profile_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// PROFILE AVATAR XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const profile_avatar_xrefPartialSchema = profile_avatar_xrefSchema.partial();
// PROFILE AVATAR XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const profile_avatar_xrefOptionalDefaultsSchema = profile_avatar_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// PROFILE SAVED SCHEMA
/////////////////////////////////////////
export const profile_savedSchema = z.object({
    uuid: z.string().uuid(),
    profile_uuid: z.string(),
    movie_uuid: z.string().nullish(),
    tv_uuid: z.string().nullish(),
    is_archived: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// PROFILE SAVED PARTIAL SCHEMA
/////////////////////////////////////////
export const profile_savedPartialSchema = profile_savedSchema.partial();
// PROFILE SAVED OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const profile_savedOptionalDefaultsSchema = profile_savedSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_archived: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// PROFILE VOTE SCHEMA
/////////////////////////////////////////
export const profile_voteSchema = z.object({
    uuid: z.string().uuid(),
    profile_uuid: z.string(),
    movie_uuid: z.string().nullish(),
    tv_uuid: z.string().nullish(),
    vote: z.union([z.literal(-1), z.literal(1)]),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// PROFILE VOTE PARTIAL SCHEMA
/////////////////////////////////////////
export const profile_votePartialSchema = profile_voteSchema.partial();
// PROFILE VOTE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const profile_voteOptionalDefaultsSchema = profile_voteSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// PROFILE WATCH HISTORY SCHEMA
/////////////////////////////////////////
export const profile_watch_historySchema = z.object({
    uuid: z.string().uuid(),
    profile_uuid: z.string(),
    movie_uuid: z.string().nullish(),
    episode_uuid: z.string().nullish(),
    progress: z.number().min(0).max(100),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// PROFILE WATCH HISTORY PARTIAL SCHEMA
/////////////////////////////////////////
export const profile_watch_historyPartialSchema = profile_watch_historySchema.partial();
// PROFILE WATCH HISTORY OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const profile_watch_historyOptionalDefaultsSchema = profile_watch_historySchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// REGION SCHEMA
/////////////////////////////////////////
export const regionSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string().trim().min(1).max(255),
    slug: z.string().trim().regex(/^[a-zA-Z0-9_-]*$/, { message: "Can only contain letters, numbers, underscores and hyphens" }).min(1).max(255),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// REGION PARTIAL SCHEMA
/////////////////////////////////////////
export const regionPartialSchema = regionSchema.partial();
// REGION OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const regionOptionalDefaultsSchema = regionSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SAVED SCHEMA
/////////////////////////////////////////
export const savedSchema = z.object({
    uuid: z.string().uuid(),
    user_uuid: z.string(),
    crossword_uuid: z.string(),
    is_archived: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SAVED PARTIAL SCHEMA
/////////////////////////////////////////
export const savedPartialSchema = savedSchema.partial();
// SAVED OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const savedOptionalDefaultsSchema = savedSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_archived: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SEASON SCHEMA
/////////////////////////////////////////
export const seasonSchema = z.object({
    uuid: z.string().uuid(),
    version_uuid: z.string().nullish(),
    title: z.string().trim().min(1).max(100),
    admin_title: z.string().trim().min(0).max(100).transform(value => value === '' ? null : value).nullish(),
    short_description: z.string().trim().min(0).max(500).transform(value => value === '' ? null : value).nullish(),
    long_description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    is_banned: z.boolean(),
    is_published: z.boolean(),
    is_trashed: z.boolean(),
    show_ads: z.boolean(),
    premier_date: z.coerce.date(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    purchasable: z.boolean(),
    purchase_price_default: z.number().min(0.0).max(100),
    purchase_price_us: z.number().min(0.0).max(100).nullable(),
    rentable: z.boolean(),
    rent_price_default: z.number().min(0.0).max(100),
    rent_price_us: z.number().min(0.0).max(100).nullable(),
    free_for_subscriber: z.boolean(),
    tv_uuid: z.string().nullish(),
    number: z.number().min(1).nullish(),
});
/////////////////////////////////////////
// SEASON PARTIAL SCHEMA
/////////////////////////////////////////
export const seasonPartialSchema = seasonSchema.partial();
// SEASON OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const seasonOptionalDefaultsSchema = seasonSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_banned: z.boolean().optional(),
    is_published: z.boolean().optional(),
    is_trashed: z.boolean().optional(),
    show_ads: z.boolean().optional(),
    premier_date: z.coerce.date().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
    purchasable: z.boolean().optional(),
    purchase_price_default: z.number().min(0.0).max(100).optional(),
    purchase_price_us: z.number().min(0.0).max(100).nullable().optional(),
    rentable: z.boolean().optional(),
    rent_price_default: z.number().min(0.0).max(100).optional(),
    rent_price_us: z.number().min(0.0).max(100).nullable().optional(),
    free_for_subscriber: z.boolean().optional(),
}));
/////////////////////////////////////////
// SEASON IMAGE XREF SCHEMA
/////////////////////////////////////////
export const season_image_xrefSchema = z.object({
    season_uuid: z.string(),
    upload_uuid: z.string(),
    is_thumbnail: z.boolean(),
});
/////////////////////////////////////////
// SEASON IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const season_image_xrefPartialSchema = season_image_xrefSchema.partial();
// SEASON IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const season_image_xrefOptionalDefaultsSchema = season_image_xrefSchema.merge(z.object({
    is_thumbnail: z.boolean().optional(),
}));
/////////////////////////////////////////
// SEASON PERSON XREF SCHEMA
/////////////////////////////////////////
export const season_person_xrefSchema = z.object({
    uuid: z.string().uuid(),
    season_uuid: z.string(),
    person_uuid: z.string(),
    is_actor: z.boolean(),
    is_director: z.boolean(),
    is_creator: z.boolean(),
});
/////////////////////////////////////////
// SEASON PERSON XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const season_person_xrefPartialSchema = season_person_xrefSchema.partial();
// SEASON PERSON XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const season_person_xrefOptionalDefaultsSchema = season_person_xrefSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_actor: z.boolean().optional(),
    is_director: z.boolean().optional(),
    is_creator: z.boolean().optional(),
}));
/////////////////////////////////////////
// SEASON TRAILER XREF SCHEMA
/////////////////////////////////////////
export const season_trailer_xrefSchema = z.object({
    season_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// SEASON TRAILER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const season_trailer_xrefPartialSchema = season_trailer_xrefSchema.partial();
// SEASON TRAILER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const season_trailer_xrefOptionalDefaultsSchema = season_trailer_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// SEASON USER XREF SCHEMA
/////////////////////////////////////////
export const season_user_xrefSchema = z.object({
    season_uuid: z.string(),
    user_uuid: z.string(),
});
/////////////////////////////////////////
// SEASON USER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const season_user_xrefPartialSchema = season_user_xrefSchema.partial();
// SEASON USER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const season_user_xrefOptionalDefaultsSchema = season_user_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// SETTING SCHEMA
/////////////////////////////////////////
export const settingSchema = z.object({
    uuid: z.string().uuid(),
    user_approved_by_default: z.boolean(),
    ads: z.boolean(),
    ads_adsense: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SETTING PARTIAL SCHEMA
/////////////////////////////////////////
export const settingPartialSchema = settingSchema.partial();
// SETTING OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const settingOptionalDefaultsSchema = settingSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    user_approved_by_default: z.boolean().optional(),
    ads: z.boolean().optional(),
    ads_adsense: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SHOW SERIES SCHEMA
/////////////////////////////////////////
export const show_seriesSchema = z.object({
    type: EnumShowSeriesTypeSchema,
    uuid: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    slug: z.string().trim().regex(/^[a-zA-Z0-9_-]*$/, { message: "Can only contain letters, numbers, underscores and hyphens" }).min(1).max(255),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    description_rich: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    user_uuid: z.string().nullish(),
    is_published: z.boolean(),
    start_time: z.coerce.date(),
    end_time: z.coerce.date(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SHOW SERIES PARTIAL SCHEMA
/////////////////////////////////////////
export const show_seriesPartialSchema = show_seriesSchema.partial();
// SHOW SERIES OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const show_seriesOptionalDefaultsSchema = show_seriesSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_published: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SHOW SERIES IMAGE XREF SCHEMA
/////////////////////////////////////////
export const show_series_image_xrefSchema = z.object({
    show_series_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// SHOW SERIES IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const show_series_image_xrefPartialSchema = show_series_image_xrefSchema.partial();
// SHOW SERIES IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const show_series_image_xrefOptionalDefaultsSchema = show_series_image_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// SHOW SERIES SHOW XREF SCHEMA
/////////////////////////////////////////
export const show_series_show_xrefSchema = z.object({
    uuid: z.string().uuid(),
    sequence: z.number().min(1),
    show_series_uuid: z.string(),
    movie_uuid: z.string().nullish(),
    tv_uuid: z.string().nullish(),
    season_uuid: z.string().nullish(),
    episode_uuid: z.string().nullish(),
    series_uuid: z.string().nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SHOW SERIES SHOW XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const show_series_show_xrefPartialSchema = show_series_show_xrefSchema.partial();
// SHOW SERIES SHOW XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const show_series_show_xrefOptionalDefaultsSchema = show_series_show_xrefSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// TAG SCHEMA
/////////////////////////////////////////
export const tagSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string().trim().min(1).max(255),
    slug: z.string().trim().regex(/^[a-zA-Z0-9_-]*$/, { message: "Can only contain letters, numbers, underscores and hyphens" }).min(1).max(255),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// TAG PARTIAL SCHEMA
/////////////////////////////////////////
export const tagPartialSchema = tagSchema.partial();
// TAG OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tagOptionalDefaultsSchema = tagSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// TV SCHEMA
/////////////////////////////////////////
export const tvSchema = z.object({
    uuid: z.string().uuid(),
    version_uuid: z.string().nullish(),
    title: z.string().trim().min(1).max(100),
    admin_title: z.string().trim().min(0).max(100).transform(value => value === '' ? null : value).nullish(),
    search_token: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    short_description: z.string().trim().min(0).max(500).transform(value => value === '' ? null : value).nullish(),
    long_description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    is_banned: z.boolean(),
    is_published: z.boolean(),
    is_trashed: z.boolean(),
    show_ads: z.boolean(),
    premier_date: z.coerce.date(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    purchasable: z.boolean(),
    purchase_price_default: z.number().min(0.0).max(100),
    purchase_price_us: z.number().min(0.0).max(100).nullable(),
    rentable: z.boolean(),
    rent_price_default: z.number().min(0.0).max(100),
    rent_price_us: z.number().min(0.0).max(100).nullable(),
    owner_user_uuid: z.string().nullish(),
    purchase_commission_percent: z.number().min(0).max(100),
    rent_commission_percent: z.number().min(0).max(100),
    free_for_subscriber: z.boolean(),
});
/////////////////////////////////////////
// TV PARTIAL SCHEMA
/////////////////////////////////////////
export const tvPartialSchema = tvSchema.partial();
// TV OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tvOptionalDefaultsSchema = tvSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_banned: z.boolean().optional(),
    is_published: z.boolean().optional(),
    is_trashed: z.boolean().optional(),
    show_ads: z.boolean().optional(),
    premier_date: z.coerce.date().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
    purchasable: z.boolean().optional(),
    purchase_price_default: z.number().min(0.0).max(100).optional(),
    purchase_price_us: z.number().min(0.0).max(100).nullable().optional(),
    rentable: z.boolean().optional(),
    rent_price_default: z.number().min(0.0).max(100).optional(),
    rent_price_us: z.number().min(0.0).max(100).nullable().optional(),
    free_for_subscriber: z.boolean().optional(),
}));
/////////////////////////////////////////
// TV GENRE XREF SCHEMA
/////////////////////////////////////////
export const tv_genre_xrefSchema = z.object({
    tv_uuid: z.string(),
    genre_uuid: z.string(),
});
/////////////////////////////////////////
// TV GENRE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_genre_xrefPartialSchema = tv_genre_xrefSchema.partial();
// TV GENRE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_genre_xrefOptionalDefaultsSchema = tv_genre_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// TV IMAGE XREF SCHEMA
/////////////////////////////////////////
export const tv_image_xrefSchema = z.object({
    tv_uuid: z.string(),
    upload_uuid: z.string(),
    is_thumbnail: z.boolean(),
});
/////////////////////////////////////////
// TV IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_image_xrefPartialSchema = tv_image_xrefSchema.partial();
// TV IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_image_xrefOptionalDefaultsSchema = tv_image_xrefSchema.merge(z.object({
    is_thumbnail: z.boolean().optional(),
}));
/////////////////////////////////////////
// TV MPAA XREF SCHEMA
/////////////////////////////////////////
export const tv_mpaa_xrefSchema = z.object({
    tv_uuid: z.string(),
    mpaa_uuid: z.string(),
});
/////////////////////////////////////////
// TV MPAA XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_mpaa_xrefPartialSchema = tv_mpaa_xrefSchema.partial();
// TV MPAA XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_mpaa_xrefOptionalDefaultsSchema = tv_mpaa_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// TV PERSON XREF SCHEMA
/////////////////////////////////////////
export const tv_person_xrefSchema = z.object({
    uuid: z.string().uuid(),
    tv_uuid: z.string(),
    person_uuid: z.string(),
    is_actor: z.boolean(),
    is_director: z.boolean(),
    is_creator: z.boolean(),
});
/////////////////////////////////////////
// TV PERSON XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_person_xrefPartialSchema = tv_person_xrefSchema.partial();
// TV PERSON XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_person_xrefOptionalDefaultsSchema = tv_person_xrefSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_actor: z.boolean().optional(),
    is_director: z.boolean().optional(),
    is_creator: z.boolean().optional(),
}));
/////////////////////////////////////////
// TV REGION XREF SCHEMA
/////////////////////////////////////////
export const tv_region_xrefSchema = z.object({
    tv_uuid: z.string(),
    region_uuid: z.string(),
});
/////////////////////////////////////////
// TV REGION XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_region_xrefPartialSchema = tv_region_xrefSchema.partial();
// TV REGION XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_region_xrefOptionalDefaultsSchema = tv_region_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// TV TAG XREF SCHEMA
/////////////////////////////////////////
export const tv_tag_xrefSchema = z.object({
    tv_uuid: z.string(),
    tag_uuid: z.string(),
});
/////////////////////////////////////////
// TV TAG XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_tag_xrefPartialSchema = tv_tag_xrefSchema.partial();
// TV TAG XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_tag_xrefOptionalDefaultsSchema = tv_tag_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// TV TRAILER XREF SCHEMA
/////////////////////////////////////////
export const tv_trailer_xrefSchema = z.object({
    tv_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// TV TRAILER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_trailer_xrefPartialSchema = tv_trailer_xrefSchema.partial();
// TV TRAILER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_trailer_xrefOptionalDefaultsSchema = tv_trailer_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// TV USER XREF SCHEMA
/////////////////////////////////////////
export const tv_user_xrefSchema = z.object({
    tv_uuid: z.string(),
    user_uuid: z.string(),
});
/////////////////////////////////////////
// TV USER XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const tv_user_xrefPartialSchema = tv_user_xrefSchema.partial();
// TV USER XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const tv_user_xrefOptionalDefaultsSchema = tv_user_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// UPLOAD SCHEMA
/////////////////////////////////////////
export const uploadSchema = z.object({
    type: EnumUploadTypeSchema,
    uuid: z.string().uuid(),
    google_storage_key: z.string().nullish(),
    name: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    description: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    is_avatar: z.boolean(),
    sequence: z.number().min(1).nullish(),
    content_type: z.string().nullish(),
    is_public: z.boolean(),
    mux_asset_id: z.string().nullish(),
    /**
     * mux asset environment is mainly used for testing purposes where if we detect an asset is a prod/staging assets
     * we will serve a dev asset automatically so that they playback in the mux player works with dev sining tokens
     */
    mux_asset_environment: z.string().nullish(),
    user_uuid: z.string().nullish(),
    duration_seconds: z.number().int().nullish(),
    mux_playback_id: z.string().nullish(),
});
/////////////////////////////////////////
// UPLOAD PARTIAL SCHEMA
/////////////////////////////////////////
export const uploadPartialSchema = uploadSchema.partial();
// UPLOAD OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const uploadOptionalDefaultsSchema = uploadSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
    is_avatar: z.boolean().optional(),
}));
/////////////////////////////////////////
// UPLOAD LANGUAGE XREF SCHEMA
/////////////////////////////////////////
export const upload_language_xrefSchema = z.object({
    upload_uuid: z.string(),
    language_uuid: z.string(),
});
/////////////////////////////////////////
// UPLOAD LANGUAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const upload_language_xrefPartialSchema = upload_language_xrefSchema.partial();
// UPLOAD LANGUAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const upload_language_xrefOptionalDefaultsSchema = upload_language_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// USER SCHEMA
/////////////////////////////////////////
export const userSchema = z.object({
    domain_env: EnumDomainEnvDbSchema,
    uuid: z.string().uuid(),
    name: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    user_name: z.string().trim().regex(/^[a-zA-Z0-9_-]*$/, { message: "Can only contain letters, numbers, underscores and hyphens" }).min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    password: z.string().trim().min(50).transform(value => value === '' ? null : value).nullish(),
    email: z.string().trim().min(0).max(255).refine((val) => val ? validator.isEmail(val) : true, { message: 'Must be a valid email.' }).transform(value => value === '' ? null : value).nullish(),
    summary: z.string().trim().min(0).max(500).transform(value => value === '' ? null : value).nullish(),
    biography: z.string().trim().min(0).max(1000).transform(value => value === '' ? null : value).nullish(),
    is_banned: z.boolean(),
    is_approved: z.boolean(),
    email_is_confirmed: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
    facebook_user_name: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    twitter_user_name: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    coins: z.number().int().min(0),
    facebook_id: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    google_id: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    birth_date: z.coerce.date().nullish(),
    sc_stripe_customer_id: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    sc_get_started_completed: z.boolean(),
    apple_user_id: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    credits: z.number().min(0),
    phone_number: z.string().trim().min(0).max(255).refine((val) => val ? validator.isMobilePhone(val) : true, { message: 'Must be a valid phone number.' }).transform(value => value === '' ? null : value).nullish(),
    phone_number_verified: z.boolean(),
});
/////////////////////////////////////////
// USER CUSTOM VALIDATORS SCHEMA
/////////////////////////////////////////
export const userCustomValidatorsSchema = userSchema;
/////////////////////////////////////////
// USER PARTIAL SCHEMA
/////////////////////////////////////////
export const userPartialSchema = userSchema.partial();
// USER OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const userOptionalDefaultsSchema = userSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    is_banned: z.boolean().optional(),
    is_approved: z.boolean().optional(),
    email_is_confirmed: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
    coins: z.number().int().min(0).optional(),
    sc_get_started_completed: z.boolean().optional(),
    credits: z.number().min(0).optional(),
    phone_number_verified: z.boolean().optional(),
}));
/////////////////////////////////////////
// USER IMAGE XREF SCHEMA
/////////////////////////////////////////
export const user_image_xrefSchema = z.object({
    user_uuid: z.string(),
    upload_uuid: z.string(),
});
/////////////////////////////////////////
// USER IMAGE XREF PARTIAL SCHEMA
/////////////////////////////////////////
export const user_image_xrefPartialSchema = user_image_xrefSchema.partial();
// USER IMAGE XREF OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const user_image_xrefOptionalDefaultsSchema = user_image_xrefSchema.merge(z.object({}));
/////////////////////////////////////////
// USER PERMISSION SCHEMA
/////////////////////////////////////////
export const user_permissionSchema = z.object({
    permission_role: EnumPermissionRoleSchema,
    user_uuid: z.string(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// USER PERMISSION PARTIAL SCHEMA
/////////////////////////////////////////
export const user_permissionPartialSchema = user_permissionSchema.partial();
// USER PERMISSION OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const user_permissionOptionalDefaultsSchema = user_permissionSchema.merge(z.object({
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// USER SHOW PURCHASE SCHEMA
/////////////////////////////////////////
export const user_show_purchaseSchema = z.object({
    uuid: z.string().uuid(),
    user_uuid: z.string().nullish(),
    owner_user_uuid: z.string().nullish(),
    price: z.number().min(0).max(100),
    commission_percent: z.number().min(0).max(100),
    movie_uuid: z.string().nullish(),
    episode_uuid: z.string().nullish(),
    season_uuid: z.string().nullish(),
    tv_uuid: z.string().nullish(),
    country_code: z.string().trim().min(0).max(2).refine((val) => val ? validator.isISO31661Alpha2(val) : true, { message: 'Must be a valid country code.' }).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// USER SHOW PURCHASE CUSTOM VALIDATORS SCHEMA
/////////////////////////////////////////
export const user_show_purchaseCustomValidatorsSchema = user_show_purchaseSchema;
/////////////////////////////////////////
// USER SHOW PURCHASE PARTIAL SCHEMA
/////////////////////////////////////////
export const user_show_purchasePartialSchema = user_show_purchaseSchema.partial();
// USER SHOW PURCHASE OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const user_show_purchaseOptionalDefaultsSchema = user_show_purchaseSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// USER SHOW RENT SCHEMA
/////////////////////////////////////////
export const user_show_rentSchema = z.object({
    uuid: z.string().uuid(),
    user_uuid: z.string().nullish(),
    owner_user_uuid: z.string().nullish(),
    price: z.number().min(0).max(100),
    commission_percent: z.number().min(0).max(100),
    movie_uuid: z.string().nullish(),
    episode_uuid: z.string().nullish(),
    season_uuid: z.string().nullish(),
    tv_uuid: z.string().nullish(),
    country_code: z.string().trim().min(0).max(2).refine((val) => val ? validator.isISO31661Alpha2(val) : true, { message: 'Must be a valid country code.' }).transform(value => value === '' ? null : value).nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// USER SHOW RENT CUSTOM VALIDATORS SCHEMA
/////////////////////////////////////////
export const user_show_rentCustomValidatorsSchema = user_show_rentSchema;
/////////////////////////////////////////
// USER SHOW RENT PARTIAL SCHEMA
/////////////////////////////////////////
export const user_show_rentPartialSchema = user_show_rentSchema.partial();
// USER SHOW RENT OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const user_show_rentOptionalDefaultsSchema = user_show_rentSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SC BUSINESS SCHEMA
/////////////////////////////////////////
export const sc_businessSchema = z.object({
    platform: EnumSCPlatformDbSchema,
    id: z.string().trim().min(1).max(255),
    user_uuid: z.string(),
    /**
     * instagram_account_id is The instagram professional acount ID, <IG_ID>. This ID is value of the id field received in webhook notifications for this account.
     */
    instagram_account_id: z.string().trim().min(1).max(255).nullish(),
    instagram_long_lived_access_token: z.string().nullish(),
    instagram_long_lived_access_token_expires_at: z.coerce.date().nullish(),
    instagram_permissions_csv: z.string().nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SC BUSINESS PARTIAL SCHEMA
/////////////////////////////////////////
export const sc_businessPartialSchema = sc_businessSchema.partial();
// SC BUSINESS OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const sc_businessOptionalDefaultsSchema = sc_businessSchema.merge(z.object({
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SC BUSINESS WORKFLOW SCHEMA
/////////////////////////////////////////
export const sc_business_workflowSchema = z.object({
    business_id: z.string(),
    instructions_text: z.string().trim().min(1).max(1000),
    reply_as_message: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SC BUSINESS WORKFLOW PARTIAL SCHEMA
/////////////////////////////////////////
export const sc_business_workflowPartialSchema = sc_business_workflowSchema.partial();
// SC BUSINESS WORKFLOW OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const sc_business_workflowOptionalDefaultsSchema = sc_business_workflowSchema.merge(z.object({
    reply_as_message: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SC POST WORKFLOW SCHEMA
/////////////////////////////////////////
export const sc_post_workflowSchema = z.object({
    post_id: z.string().trim().min(1).max(255),
    business_id: z.string().trim().min(1).max(255),
    instructions_text: z.string().trim().min(1).max(1000),
    reply_as_message: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SC POST WORKFLOW PARTIAL SCHEMA
/////////////////////////////////////////
export const sc_post_workflowPartialSchema = sc_post_workflowSchema.partial();
// SC POST WORKFLOW OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const sc_post_workflowOptionalDefaultsSchema = sc_post_workflowSchema.merge(z.object({
    reply_as_message: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
/////////////////////////////////////////
// SC COMMENT SCHEMA
/////////////////////////////////////////
export const sc_commentSchema = z.object({
    uuid: z.string().uuid(),
    business_id: z.string().trim().min(1).max(255).nullish(),
    comment_id: z.string().trim().min(1).max(255),
    commenter_id: z.string().trim().min(0).max(255).transform(value => value === '' ? null : value).nullish(),
    reply_as_message: z.boolean(),
    /**
     * the text that should be sent to the comment to notify the user that their comment has been replied to in DM
     */
    reply_as_message_comment_text: z.string().trim().min(1).max(500).nullish(),
    reply_text: z.string().trim().min(1).max(500),
    reply_attempts: z.number().int(),
    reply_posted: z.boolean(),
    reply_at: z.coerce.date(),
    workflow_business_id: z.string().nullish(),
    workflow_post_id: z.string().nullish(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/////////////////////////////////////////
// SC COMMENT CUSTOM VALIDATORS SCHEMA
/////////////////////////////////////////
export const sc_commentCustomValidatorsSchema = sc_commentSchema.refine((data) => { if (data.reply_as_message && !data.commenter_id) {
    return false;
} return true; }, { message: "commenter_id is required when reply_as_message is true" });
/////////////////////////////////////////
// SC COMMENT PARTIAL SCHEMA
/////////////////////////////////////////
export const sc_commentPartialSchema = sc_commentSchema.partial();
// SC COMMENT OPTIONAL DEFAULTS SCHEMA
//------------------------------------------------------
export const sc_commentOptionalDefaultsSchema = sc_commentSchema.merge(z.object({
    uuid: z.string().uuid().optional(),
    reply_as_message: z.boolean().optional(),
    reply_attempts: z.number().int().optional(),
    reply_posted: z.boolean().optional(),
    created_at: z.coerce.date().optional(),
    updated_at: z.coerce.date().optional(),
}));
//# sourceMappingURL=index.js.map